import React, { useEffect, useState } from 'react'
import { ContentModal, Flex, Title, P, Button } from 'ui/acro'
import styled from 'styled-components'
import { css } from '@styled-system/css'
import { useModal_deprecated } from 'hooks'
import { axios } from 'api'
import ModalImage from '../AlertModal/SurveyOutOfDate/ModalImage'
import { sign_in_event, verify_account_event } from 'gtm/pushEvents'
import {
  change_braze_user,
  verify_account_braze_event
} from 'braze/brazeEvents'

const StyledTitle = styled(Title)(
  {
    fontFamily: 'Proxima-Nova',
    fontStyle: 'normal',
    fontWeight: 600
  },
  css({
    fontSize: ['24px', '24px', '32px'],
    letterSpacing: ['-0.72px', '-0.72px', '-0.96px'],
    lineHeight: ['135%', '135%', '40px']
  })
)

const StyledButton = styled(Button)({
  fontFamily: 'Proxima-Nova',
  fontSize: '18px',
  fontStyle: 'normal',
  fontWeight: '700',
  lineHeight: '24px',
  letterSpacing: '0.9px',
  textTransform: 'uppercase',
  width: '100%',
  marginBottom: '16px',
  borderRadius: '0px'
})

const SignOutLink = styled.a`
  display: block;
  text-align: center;
  color: #3b3b3b;
  font-family: 'Proxima-Nova';
  font-size: 14px;
  font-weight: 400;
  line-height: 24px;
  letter-spacing: 0.308px;
  text-decoration: underline !important;
  margin-bottom: 16px;
  cursor: pointer;

  &:visited {
    color: #3b3b3b;
  }
`

const NewUserPopup = ({
  handleClose = () => {},
  showSignOut = false,
  email = '',
  createAccountOnContinue = false
}) => {
  const [alreadySeen, setAlreadySeen] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)

  const goToSurvey = () => {
    setAlreadySeen(true)
    window.location.href = '/surveys/onboarding'
  }

  const handleContinue = async () => {
    if (isSubmitting) return

    if (!createAccountOnContinue) {
      handleClose()
      goToSurvey()
      return
    }

    // Silent Alo session: create AWC account only on Continue
    setIsSubmitting(true)
    try {
      const res = await axios.post('/alo_token_auth/complete_silent_signup')
      if (!res?.data?.success) {
        setIsSubmitting(false)
        return
      }

      const { accountJustLinked, linkedUser, user } = res.data
      if (accountJustLinked) {
        verify_account_event(false, user?.userAloTier || 'na', user)
        verify_account_braze_event(false, user?.userAloTier || 'na', user)
      }
      sign_in_event({ aa_linked_user: linkedUser, user })
      change_braze_user(user)

      goToSurvey()
    } catch (err) {
      console.error(err)
      setIsSubmitting(false)
    }
  }

  const handleSignOut = async (e) => {
    e.preventDefault()
    e.stopPropagation()
    if (isSubmitting) return

    if (createAccountOnContinue) {
      // Do not create an account — reject pending silent welcome
      try {
        await axios.post('/alo_token_auth/reject_silent_signup')
      } catch (err) {
        console.error(err)
      }
      setAlreadySeen(true)
      window.location.href = '/signin'
      return
    }

    setAlreadySeen(true)
    window.location.href = '/signout?redirect=signin'
  }

  const alertModal = useModal_deprecated(() => {
    if (!createAccountOnContinue) {
      handleContinue()
    }
  })

  useEffect(() => {
    if (!alreadySeen) {
      alertModal.open()
    }
  }, [alreadySeen])

  if (alreadySeen) return null

  return (
    <ContentModal
      useModal={alertModal}
      overflow='hidden'
      style={{ maxWidth: '862px', borderRadius: 0, border: 'none' }}
    >
      <Flex flexDirection={['column', null, 'row']}>
        <ModalImage />
        <Flex
          width={['100%', null, '50%']}
          flexDirection='column'
          justifyContent='center'
          px={['28px', null, '48px']}
        >
          <StyledTitle mb={['4px', null, '8px']} wordBreak='break-word'>
            You’re in the club.
          </StyledTitle>

          <P mb={['16px', null, '28px']}>
            Welcome to ALO Wellness Club. Explore thousands of yoga, fitness,
            and meditation classes — anytime, anywhere.
          </P>

          <Flex flexDirection='column'>
            <StyledButton
              onClick={handleContinue}
              variant='primary'
              id='new_user_welcome_continue'
              aria-label='Continue'
              style={{ borderRadius: 0 }}
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Please wait…' : 'Continue'}
            </StyledButton>
            {showSignOut && (
              <>
                {email && (
                  <P
                    mb='4px'
                    style={{
                      textAlign: 'center',
                      fontSize: '14px',
                      color: '#3b3b3b'
                    }}
                  >
                    {createAccountOnContinue
                      ? `Continuing as ${email}`
                      : `Signed in as ${email}`}
                  </P>
                )}
                <SignOutLink
                  href='/signin'
                  onClick={handleSignOut}
                  id='new_user_welcome_signout'
                >
                  Not you? Sign out
                </SignOutLink>
              </>
            )}
          </Flex>
        </Flex>
      </Flex>
    </ContentModal>
  )
}

export default NewUserPopup
